import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { Modal, Image } from 'react-bootstrap';
import CloseIcon from '../../../assets/images/prompt_close_icon.svg';
import PromptButton, { BUTTON_TYPES } from 'components/base/prompt/BaseButtons';
import { PermissionCodes } from 'config/PermissionCodes';
import Loading from 'components/base/Loading';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import { hasError } from 'components/base/ErrorFieldMessage';
import { LevelErrorHandleFields } from './LevelErrorHandle';
import {
  DISPLAY_HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL,
  DISPLAY_LEVEL_CALCULATION_SOURCE,
  DISPLAY_MEMBERSHIP_PERIOD_MODE,
  HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL,
  LEVEL_CALCULATION_SOURCE,
  MEMBERSHIP_PERIOD_MODE,
  MonthOptions
} from 'config/CustomEnums';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import CustomRadios from 'components/base/CustomRadios';
import CustomSwitchButton from 'components/base/CustomSwitchButton';
import { OnlyDropdown } from 'components/base/ActionsDropdown';
import './LevelGlobalSettingModal.scss';


const getMonthEndDay = (month) => {
  const monthInt = parseInt(month || 1);
  if ([1, 3, 5, 7, 8, 10, 12].includes(monthInt)) {
    return 31;
  }
  if ([4, 6, 9, 11].includes(monthInt)) {
    return 30;
  }
  return 28;
}

const updateLevelGlobalSettingValidate = ({
  getValues,
  setError,
  clearErrors
}) => {
  const membershipPeriodModeField = "membershipPeriodMode";
  const durationField = "dynamicDuration";
  const refundDayField = "handlingExpiredPeriodXDayAfterRefund";

  clearErrors([
    durationField,
    refundDayField,
  ]);

  const [
    membershipPeriodMode,
    duration,
    refundDay,
  ] = getValues([
    membershipPeriodModeField,
    durationField,
    refundDayField
  ]);

  let isValid = true;

  if (membershipPeriodMode === MEMBERSHIP_PERIOD_MODE.dynamic) {
    if (!duration || duration <= 0) {
      isValid = false;
      setError(
        durationField,
        {
          type: 'require',
          message: LevelErrorHandleFields.number,
        },
      );
    }

    if ((!refundDay && refundDay != 0) || refundDay < 0) {
      isValid = false;
      setError(
        refundDayField,
        {
          type: 'require',
          message: LevelErrorHandleFields.number,
        },
      );
    }
  }

  return isValid;
};


const LevelGlobalSettingSection = ({ }) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const membershipPeriodModeField = "membershipPeriodMode";
  const fixedPeriodStartMonthField = "fixedPeriodStartMonth";
  const fixedPeriodStartDayField = "fixedPeriodStartDay";
  const dynamicDurationField = "dynamicDuration";
  const extendMembershipOnUpgradeField = "extendMembershipOnUpgrade";
  const levelCalculationSourceField = "levelCalculationSource";
  const handlingCalculationSourceAfterRenewalField = "handlingCalculationSourceAfterRenewal";
  const enableEasyUpgradeField = "enableEasyUpgrade";
  const handlingExpiredPeriodXDayAfterRefundField = "handlingExpiredPeriodXDayAfterRefund";

  const watchMembershipPeriodMode = watch(membershipPeriodModeField);
  const watchFixedPeriodStartMonth = watch(fixedPeriodStartMonthField);
  const watchFixedPeriodStartDay = watch(fixedPeriodStartDayField);
  const watchDynamicDuration = watch(dynamicDurationField);
  const watchExtendMembershipOnUpgrade = watch(extendMembershipOnUpgradeField);
  const watchLevelCalculationSource = watch(levelCalculationSourceField);
  const watchHandlingCalculationSourceAfterRenewal = watch(handlingCalculationSourceAfterRenewalField);
  const watchEnableEasyUpgrade = watch(enableEasyUpgradeField);
  const watchHandlingExpiredPeriodXDayAfterRefund = watch(handlingExpiredPeriodXDayAfterRefundField);

  const monthDisplay = MonthOptions.find(item => item.value == watchFixedPeriodStartMonth)?.name || "";

  const dayOptions = [];
  const dayLength = getMonthEndDay(watchFixedPeriodStartMonth);
  for (let i = 1; i <= dayLength; i++) {
    dayOptions.push({
      name: i,
      value: i,
    })
  }

  let endMonth = null;
  let endDay = null;
  if (watchFixedPeriodStartMonth && watchFixedPeriodStartDay) {
    if (watchFixedPeriodStartDay == 1) {
      endMonth = watchFixedPeriodStartMonth - 1;
      if (endMonth == 0) {
        endMonth = 12;
      }

      endDay = getMonthEndDay(endMonth);
    }
    else {
      endMonth = watchFixedPeriodStartMonth;
      endDay = watchFixedPeriodStartDay - 1;
    }
  }
  const endMonthDisplay = MonthOptions.find(item => item.value == endMonth)?.name || "";

  return (
    <div>
      <CustomTitleLabel title="Mode of membership period" />
      <div className="second-section_description">
        If any change is made after the level is created, the setting will be applied immediately for new members and after renewal for old members.
      </div>
      <div className="d-flex level-global-setting-margin-top">
        <CustomRadios
          default={watchMembershipPeriodMode || MEMBERSHIP_PERIOD_MODE.fixed}
          onChange={(value) => {
            setValue(membershipPeriodModeField, value, { shouldDirty: true })
          }}
          options={[
            {
              label: DISPLAY_MEMBERSHIP_PERIOD_MODE[MEMBERSHIP_PERIOD_MODE.fixed],
              value: MEMBERSHIP_PERIOD_MODE.fixed,
              popoverTips: "All memberships expire on the same day at 23:59:59.",
            },
            {
              label: DISPLAY_MEMBERSHIP_PERIOD_MODE[MEMBERSHIP_PERIOD_MODE.dynamic],
              value: MEMBERSHIP_PERIOD_MODE.dynamic,
              popoverTips: "All memberships expire after a number of days at 23:59:59 since sign up.",
            },
          ]}
        />
      </div>

      {
        watchMembershipPeriodMode === MEMBERSHIP_PERIOD_MODE.dynamic
          ? <>
            <CustomTitleWithInput
              type='number'
              customClass="level-global-setting-margin-right"
              title="Duration"
              defaultValue={`${watchDynamicDuration}`}
              setValue={(value) => {
                setValue(dynamicDurationField, value, { shouldDirty: true, });
              }}
              unit="days"
              error={{
                id: dynamicDurationField,
                error: hasError(errors, dynamicDurationField),
                message: LevelErrorHandleFields.number,
              }}
            />
            <CustomTitleLabel title="Extend membership period when upgrade" />
            <div className="second-section_description">
              Membership period will be extended for same duration after upgrade.
            </div>
            <div className='level-global-setting-margin-top'>
              <CustomSwitchButton
                defaultChecked={watchExtendMembershipOnUpgrade}
                onChange={(isChecked) => {
                  setValue(extendMembershipOnUpgradeField, isChecked, { shouldDirty: true });
                }}
              />
            </div>
          </>
          : <>
            <CustomTitleLabel title="Period" />
            <div className='level-global-setting-period'>
              <div className='section-short-description level-global-setting-margin-right'>From</div>
              <OnlyDropdown
                toggleText={monthDisplay}
                options={MonthOptions}
                setValue={(value) => {
                  setValue(fixedPeriodStartMonthField, value, { shouldDirty: true });
                  const maxDays = getMonthEndDay(value);
                  if (watchFixedPeriodStartDay && watchFixedPeriodStartDay > maxDays) {
                    setValue(fixedPeriodStartDayField, maxDays, { shouldDirty: true });
                  }
                }}
                customClass="level-global-setting-month-dropdown"
              />
              <OnlyDropdown
                toggleText={watchFixedPeriodStartDay}
                options={dayOptions}
                setValue={(value) => {
                  setValue(fixedPeriodStartDayField, value, { shouldDirty: true });
                }}
                customClass="level-global-setting-day-dropdown"
              />
              <div className='section-short-description level-global-setting-margin-right'>to</div>
              <input
                type="text"
                className={`custom-markdown-area-title custom-markdown-area-title-short level-global-setting-margin-right`}
                disabled
                value={endMonthDisplay}
              />
              <input
                type="text"
                className={`custom-markdown-area-title custom-number-input-short`}
                disabled
                value={endDay}
              />
            </div>
          </>
      }

      <CustomTitleLabel title="Which of the followings is used to process upgrade and renewal (for condition 1 only)?" />
      <div className="d-flex">
        <CustomRadios
          default={watchLevelCalculationSource || LEVEL_CALCULATION_SOURCE.spending}
          onChange={(value) => {
            setValue(levelCalculationSourceField, value, { shouldDirty: true })
          }}
          options={[
            {
              label: DISPLAY_LEVEL_CALCULATION_SOURCE[LEVEL_CALCULATION_SOURCE.spending],
              value: LEVEL_CALCULATION_SOURCE.spending
            },
            {
              label: DISPLAY_LEVEL_CALCULATION_SOURCE[LEVEL_CALCULATION_SOURCE.pp],
              value: LEVEL_CALCULATION_SOURCE.pp
            },
          ]}
        />
      </div>

      <CustomTitleLabel title="Handling of spending / progression points (PP) after renewal" />
      <div className="d-flex">
        <CustomRadios
          default={watchHandlingCalculationSourceAfterRenewal || HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL.resetToZero}
          onChange={(value) => {
            setValue(handlingCalculationSourceAfterRenewalField, value, { shouldDirty: true })
          }}
          options={[
            {
              label: DISPLAY_HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL[HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL.resetToZero],
              value: HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL.resetToZero
            },
            // {
            //   label: DISPLAY_HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL[HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL.unchanged],
            //   value: HANDLING_CALCULATION_SOURCE_AFTER_RENEWAL.unchanged
            // },
          ]}
        />
      </div>

      <CustomTitleLabel title="Enable easy upgrade after renewal" />
      <div className="second-section_description">
        If toggle on, the amount of spending / PP required for upgrade (for condition 1 only) after renewal will be the difference between the entry requirements of the target level & current level.
      </div>
      <div className='level-global-setting-margin-top'>
        <CustomSwitchButton
          defaultChecked={watchEnableEasyUpgrade}
          onChange={(isChecked) => {
            setValue(enableEasyUpgradeField, isChecked, { shouldDirty: true });
          }}
        />
      </div>
      {
        watchMembershipPeriodMode === MEMBERSHIP_PERIOD_MODE.dynamic
          ? <>
            <CustomTitleWithInput
              type='number'
              customLineClass="level-global-setting-handling-expired-input"
              customClass="level-global-setting-margin-right"
              title="Special handling of expired membership period due to refund"
              tips="If a refund will cause downgrade, the closest membership period of same level (since last renewal if any) will be resumed. But if the membership period has expired, new membership period will be refund date + N days."
              defaultValue={`${watchHandlingExpiredPeriodXDayAfterRefund}`}
              setValue={(value) => {
                setValue(handlingExpiredPeriodXDayAfterRefundField, value, { shouldDirty: true, });
              }}
              per="Refund date +"
              unit="days"
              error={{
                id: handlingExpiredPeriodXDayAfterRefundField,
                error: hasError(errors, handlingExpiredPeriodXDayAfterRefundField),
                message: LevelErrorHandleFields.number,
              }}
            />
          </>
          : <>
          </>
      }
    </div>
  );
}


const LevelGlobalSetting = ({
  levelGlobalSettingLoading,
  levelGlobalSetting,
  show,
  setShow,
  successAction = () => { },
}) => {
  const dispatch = useDispatch();

  const methods = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: levelGlobalSetting,
  });

  const { getValues, setError, clearErrors } = methods;

  useEffect(() => {
    if (show) {
      dispatch({
        type: 'levels/getLevelGlobalSetting',
        payload: {},
      });
    }
  }, [dispatch, show]);

  useEffect(() => {
    methods.reset(levelGlobalSetting);
  }, [dispatch, methods, levelGlobalSetting])

  const onClose = () => {
    setShow(false);
    dispatch({
      type: 'levels/updateState',
      payload: {
        levelGlobalSettingLoading: false,
        levelGlobalSetting: {},
      },
    })
  }

  const saveAction = () => {
    const isValid = updateLevelGlobalSettingValidate({
      getValues,
      setError,
      clearErrors
    });

    if (!isValid) {
      return;
    };

    dispatch({
      type: 'levels/updateLevelGlobalSetting',
      payload: {
        data: methods.getValues(),
        afterAction: () => {
          onClose();
          successAction();
        },
      },
    })
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop='static'
    >
      <Modal.Body className={`base-prompt-container`}>
        <Image
          src={CloseIcon}
          className="base-prompt-close-button"
          onClick={onClose}
        />
        <div className="first-section-title">Edit Level Global Setting</div>

        <div className='level-global-setting-container'>
          {
            levelGlobalSettingLoading
              ? <Loading />
              : <FormProvider {...methods}>
                <form
                  onSubmit={methods.handleSubmit((data) => {
                    console.log(data);
                  })}
                >
                  <LevelGlobalSettingSection />
                </form>
              </FormProvider>
          }
        </div>

        <div className="base-prompt-buttons">
          <PromptButton
            title='Cancel'
            action={onClose}
            type={BUTTON_TYPES.secondary}
          />
          <PromptButton
            title='Confirm'
            action={saveAction}
            requires={PermissionCodes.changeLevel}
            type={BUTTON_TYPES.main}
          />
        </div>

      </Modal.Body>
    </Modal>
  );
}

const mapPropsToState = (state) => ({
  levelGlobalSettingLoading: state.levels.levelGlobalSettingLoading,
  levelGlobalSetting: state.levels.levelGlobalSetting,
});

export default connect(mapPropsToState)(LevelGlobalSetting);
